import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { LogOut, Trash2, ShieldCheck, ShieldAlert, Edit, Eye, Users, Trophy, BarChart3, UserCog, Plus, Lock, Key, Copy, RefreshCw, Link2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AUTO_REFRESH_INTERVAL = 10000; // 10 seconds

export default function AdminPanel() {
  const navigate = useNavigate();
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [teams, setTeams] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [banAppeals, setBanAppeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTeamId, setDeleteTeamId] = useState(null);
  const [deleteAdminId, setDeleteAdminId] = useState(null);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [banUserId, setBanUserId] = useState(null);
  const [banReason, setBanReason] = useState('');
  const [viewAppeal, setViewAppeal] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewTeam, setViewTeam] = useState(null);
  const [editTeam, setEditTeam] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Admin user management
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);
  const [newAdmin, setNewAdmin] = useState({ email: '', password: '', name: '' });
  
  // API Token management
  const [apiToken, setApiToken] = useState(null);
  const [apiTokenActive, setApiTokenActive] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  // Auto-refresh data every 10 seconds
  useEffect(() => {
    if (currentAdmin) {
      loadData();
      
      // Set up auto-refresh interval
      const intervalId = setInterval(() => {
        loadDataSilent(); // Silent refresh without showing loading state
      }, AUTO_REFRESH_INTERVAL);
      
      return () => clearInterval(intervalId);
    }
  }, [statusFilter, currentAdmin]);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin-auth/me`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        navigate('/admin');
        return;
      }
      
      const data = await response.json();
      setCurrentAdmin(data);
    } catch (error) {
      console.error('Auth check error:', error);
      navigate('/admin');
    }
  };

  const loadData = async () => {
    try {
      const [teamsRes, statsRes, adminsRes, tokenRes, usersRes, appealsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/teams?status_filter=${statusFilter}`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/statistics`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin-users`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/api-token`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/users`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/ban-appeals`, { credentials: 'include' })
      ]);
      
      if (teamsRes.ok) {
        const teamsData = await teamsRes.json();
        setTeams(teamsData);
      }
      
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStatistics(statsData);
      }
      
      if (adminsRes.ok) {
        const adminsData = await adminsRes.json();
        setAdminUsers(adminsData);
      }
      
      if (tokenRes.ok) {
        const tokenData = await tokenRes.json();
        setApiToken(tokenData.token);
        setApiTokenActive(tokenData.is_active);
      }
      
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData.users || []);
      }
      
      if (appealsRes.ok) {
        const appealsData = await appealsRes.json();
        setBanAppeals(appealsData.appeals || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Silent data refresh (no loading state, no error toast)
  const loadDataSilent = async () => {
    try {
      const [teamsRes, statsRes, adminsRes, tokenRes, usersRes, appealsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/teams?status_filter=${statusFilter}`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/statistics`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin-users`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/api-token`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/users`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/admin/ban-appeals`, { credentials: 'include' })
      ]);
      
      if (teamsRes.ok) {
        const teamsData = await teamsRes.json();
        setTeams(teamsData);
      }
      
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStatistics(statsData);
      }
      
      if (adminsRes.ok) {
        const adminsData = await adminsRes.json();
        setAdminUsers(adminsData);
      }
      
      if (tokenRes.ok) {
        const tokenData = await tokenRes.json();
        setApiToken(tokenData.token);
        setApiTokenActive(tokenData.is_active);
      }
      
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData.users || []);
      }
      
      if (appealsRes.ok) {
        const appealsData = await appealsRes.json();
        setBanAppeals(appealsData.appeals || []);
      }
    } catch (error) {
      console.error('Silent refresh error:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/admin-auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/admin');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleDeleteTeam = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/teams/${deleteTeamId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to delete team');
      
      toast.success('Time excluído com sucesso');
      setTeams(teams.filter(t => t.team_id !== deleteTeamId));
      setDeleteTeamId(null);
      loadData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Erro ao excluir time');
    }
  };

  const handleUpdateTeam = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/teams/${editTeam.team_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          team_name: editTeam.team_name
        })
      });
      
      if (!response.ok) throw new Error('Failed to update team');
      
      toast.success('Time atualizado!');
      setEditTeam(null);
      loadData();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Erro ao atualizar');
    }
  };

  // Admin user management handlers
  const handleCreateAdmin = async () => {
    if (!newAdmin.email || !newAdmin.password || !newAdmin.name) {
      toast.error('Preencha todos os campos');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newAdmin)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create admin');
      }
      
      toast.success('Administrador criado com sucesso!');
      setShowAddAdmin(false);
      setNewAdmin({ email: '', password: '', name: '' });
      loadData();
    } catch (error) {
      console.error('Create admin error:', error);
      toast.error(error.message || 'Erro ao criar administrador');
    }
  };

  const handleUpdateAdmin = async () => {
    if (!editAdmin.name) {
      toast.error('Nome é obrigatório');
      return;
    }

    try {
      const updateData = { name: editAdmin.name };
      if (editAdmin.email !== editAdmin.originalEmail) {
        updateData.email = editAdmin.email;
      }
      if (editAdmin.newPassword) {
        updateData.password = editAdmin.newPassword;
      }

      const response = await fetch(`${BACKEND_URL}/api/admin-users/${editAdmin.admin_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update admin');
      }
      
      toast.success('Administrador atualizado!');
      setEditAdmin(null);
      loadData();
    } catch (error) {
      console.error('Update admin error:', error);
      toast.error(error.message || 'Erro ao atualizar');
    }
  };

  const handleDeleteAdmin = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin-users/${deleteAdminId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete admin');
      }
      
      toast.success('Administrador excluído');
      setDeleteAdminId(null);
      loadData();
    } catch (error) {
      console.error('Delete admin error:', error);
      toast.error(error.message || 'Erro ao excluir');
    }
  };

  // User management handlers
  const handleBanUser = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${banUserId}/ban`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: banReason })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to ban user');
      }
      
      toast.success('Usuário banido com sucesso');
      setBanUserId(null);
      setBanReason('');
      loadData();
    } catch (error) {
      console.error('Ban user error:', error);
      toast.error(error.message || 'Erro ao banir usuário');
    }
  };

  const handleUnbanUser = async (userId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/unban`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to unban user');
      }
      
      toast.success('Usuário desbanido com sucesso');
      loadData();
    } catch (error) {
      console.error('Unban user error:', error);
      toast.error(error.message || 'Erro ao desbanir usuário');
    }
  };

  const handleDeleteUser = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${deleteUserId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete user');
      }
      
      toast.success('Usuário excluído com sucesso');
      setDeleteUserId(null);
      loadData();
    } catch (error) {
      console.error('Delete user error:', error);
      toast.error(error.message || 'Erro ao excluir usuário');
    }
  };

  // Ban appeal handlers
  const handleRejectAppeal = async (appealId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/ban-appeals/${appealId}/reject`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to reject appeal');
      }
      
      toast.success('Reivindicação rejeitada');
      setViewAppeal(null);
      loadData();
    } catch (error) {
      console.error('Reject appeal error:', error);
      toast.error(error.message || 'Erro ao rejeitar reivindicação');
    }
  };

  const handleAcceptAppeal = async (userId) => {
    // Desbanir o usuário
    await handleUnbanUser(userId);
    // Fechar o modal
    setViewAppeal(null);
  };

  // API Token handlers
  const handleGenerateToken = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/api-token/generate`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to generate token');
      
      const data = await response.json();
      setApiToken(data.token);
      toast.success('Token gerado com sucesso!');
    } catch (error) {
      console.error('Generate token error:', error);
      toast.error('Erro ao gerar token');
    }
  };

  const handleToggleApiToken = async (active) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/api-token/toggle`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: active })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to toggle token');
      }
      
      setApiTokenActive(active);
      toast.success(active ? 'API pública ativada!' : 'API pública desativada');
    } catch (error) {
      console.error('Toggle token error:', error);
      toast.error(error.message || 'Erro ao alterar status');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para a área de transferência!');
  };

  const getPublicApiUrl = () => {
    return `${BACKEND_URL}/api/public/teams?token=${apiToken}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { icon: ShieldAlert, color: 'text-yellow-500', bg: 'bg-yellow-500/10', label: 'Rascunho' },
      registered: { icon: ShieldCheck, color: 'text-[#00FF66]', bg: 'bg-green-500/10', label: 'Cadastrado' }
    };
    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;
    
    return (
      <div className={`flex items-center gap-2 ${config.color} ${config.bg} px-2 py-1 rounded-full text-sm`}>
        <Icon className="h-4 w-4" />
        {config.label}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#090A0F]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#FF5A00] border-t-transparent mb-4" />
          <p className="text-white text-lg">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#090A0F] p-4 md:p-6" data-testid="admin-panel-page">
      <div className="noise-overlay" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>
              Painel <span className="text-[#FF5A00]">Administrativo</span>
            </h1>
            <p className="text-[#A1A1AA]">Bem-vindo, {currentAdmin?.name}</p>
          </div>
          <Button
            onClick={handleLogout}
            className="btn-secondary"
            data-testid="logout-button"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sair
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button
            onClick={() => setActiveTab('dashboard')}
            className={activeTab === 'dashboard' ? 'btn-primary' : 'btn-secondary'}
            data-testid="tab-dashboard"
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            Dashboard
          </Button>
          <Button
            onClick={() => setActiveTab('teams')}
            className={activeTab === 'teams' ? 'btn-primary' : 'btn-secondary'}
            data-testid="tab-teams"
          >
            <Trophy className="mr-2 h-4 w-4" />
            Times
          </Button>
          <Button
            onClick={() => setActiveTab('users')}
            className={`${activeTab === 'users' ? 'btn-primary' : 'btn-secondary'} relative`}
            data-testid="tab-users"
          >
            <Users className="mr-2 h-4 w-4" />
            Usuários
            {banAppeals.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                {banAppeals.length}
              </span>
            )}
          </Button>
          <Button
            onClick={() => setActiveTab('admins')}
            className={activeTab === 'admins' ? 'btn-primary' : 'btn-secondary'}
            data-testid="tab-admins"
          >
            <UserCog className="mr-2 h-4 w-4" />
            Administradores
          </Button>
          <Button
            onClick={() => setActiveTab('api')}
            className={activeTab === 'api' ? 'btn-primary' : 'btn-secondary'}
            data-testid="tab-api"
          >
            <Key className="mr-2 h-4 w-4" />
            API Pública
          </Button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && statistics && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="glass-card border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-[#A1A1AA] text-sm font-medium flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-[#FF5A00]" />
                    Total de Times
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-white">{statistics.total_teams}</p>
                </CardContent>
              </Card>

              <Card className="glass-card border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-[#A1A1AA] text-sm font-medium flex items-center gap-2">
                    <Users className="h-4 w-4 text-[#FF5A00]" />
                    Total de Usuários
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-white">{statistics.total_users}</p>
                </CardContent>
              </Card>

              <Card className="glass-card border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-[#A1A1AA] text-sm font-medium flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-[#00FF66]" />
                    Cadastrados
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-[#00FF66]">{statistics.teams_by_status?.registered || 0}</p>
                </CardContent>
              </Card>

              <Card className="glass-card border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-[#A1A1AA] text-sm font-medium flex items-center gap-2">
                    <ShieldAlert className="h-4 w-4 text-yellow-500" />
                    Rascunhos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-yellow-500">{statistics.teams_by_status?.draft || 0}</p>
                </CardContent>
              </Card>
            </div>

            {/* Status Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="glass-card border-white/10">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Times por Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(statistics.teams_by_status || {}).map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between">
                        {getStatusBadge(status)}
                        <span className="text-white font-semibold">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-card border-white/10">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Formações Mais Usadas</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(statistics.formations || []).slice(0, 5).map((item, index) => (
                      <div key={item.formation} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-bold ${index === 0 ? 'text-[#FF5A00]' : 'text-[#A1A1AA]'}`}>
                            #{index + 1}
                          </span>
                          <span className="text-white">{item.formation}</span>
                        </div>
                        <span className="text-[#A1A1AA]">{item.count} times</span>
                      </div>
                    ))}
                    {(!statistics.formations || statistics.formations.length === 0) && (
                      <p className="text-[#A1A1AA] text-center py-4">Nenhum dado disponível</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Players Stats */}
            <Card className="glass-card border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-lg">Jogadores Cadastrados</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <span className="text-[#A1A1AA]">Jogadores Reais</span>
                    <span className="text-2xl font-bold text-[#00FF66]">{statistics.total_real_players}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <span className="text-[#A1A1AA]">Bots</span>
                    <span className="text-2xl font-bold text-yellow-500">{statistics.total_bot_players}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Teams Tab */}
        {activeTab === 'teams' && (
          <div className="glass-card p-4 md:p-6">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <p className="text-white font-semibold text-lg">
                Total: <span className="text-[#FF5A00]">{teams.length}</span> times
              </p>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48 bg-white/5 border-white/10 text-white" data-testid="status-filter">
                  <SelectValue placeholder="Filtrar por status" />
                </SelectTrigger>
                <SelectContent className="bg-[#14151C] border-white/10">
                  <SelectItem value="all" className="text-white hover:bg-white/10">Todos</SelectItem>
                  <SelectItem value="draft" className="text-white hover:bg-white/10">Rascunho</SelectItem>
                  <SelectItem value="registered" className="text-white hover:bg-white/10">Cadastrado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white font-semibold">Nome do Time</TableHead>
                    <TableHead className="text-white font-semibold">Criador</TableHead>
                    <TableHead className="text-white font-semibold">Formação</TableHead>
                    <TableHead className="text-white font-semibold">Jogadores</TableHead>
                    <TableHead className="text-white font-semibold">Status</TableHead>
                    <TableHead className="text-white font-semibold text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teams.map((team) => (
                    <TableRow key={team.team_id} className="border-white/10 hover:bg-white/5" data-testid={`team-row-${team.team_id}`}>
                      <TableCell className="text-white font-medium">{team.team_name}</TableCell>
                      <TableCell className="text-[#A1A1AA]">
                        <div>
                          <div className="font-medium text-white">{team.creator_name}</div>
                          <div className="text-sm">{team.creator_email}</div>
                        </div>
                      </TableCell>
                      <TableCell className="text-[#A1A1AA]">{team.formation}</TableCell>
                      <TableCell className="text-white">
                        <div className="text-sm">
                          <span className={team.real_players_count >= 7 ? 'text-[#00FF66]' : 'text-yellow-500'}>
                            {team.real_players_count} reais
                          </span>
                          <span className="text-[#A1A1AA]"> / {team.total_players || 0} total</span>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(team.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            onClick={() => setViewTeam(team)}
                            variant="ghost"
                            size="sm"
                            className="text-[#A1A1AA] hover:text-white hover:bg-white/10"
                            data-testid={`view-team-${team.team_id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={() => setEditTeam({...team})}
                            variant="ghost"
                            size="sm"
                            className="text-blue-500 hover:text-blue-400 hover:bg-blue-500/10"
                            data-testid={`edit-team-${team.team_id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={() => setDeleteTeamId(team.team_id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                            data-testid={`delete-team-${team.team_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {teams.length === 0 && (
              <div className="text-center py-12">
                <p className="text-[#A1A1AA] text-lg">Nenhum time encontrado</p>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Ban Appeals Alert */}
            {banAppeals.length > 0 && (
              <div className="glass-card p-4 md:p-6 border-2 border-yellow-500/50 bg-yellow-500/5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-yellow-500/20 p-2 rounded-full">
                    <ShieldAlert className="h-6 w-6 text-yellow-500" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg">
                      Reivindicações de Banimento Pendentes
                    </h3>
                    <p className="text-yellow-400 text-sm">
                      {banAppeals.length} usuário(s) reivindicando banimento
                    </p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {banAppeals.map(appeal => (
                    <div 
                      key={appeal.id}
                      className="bg-white/5 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-medium">{appeal.user_name}</span>
                          <span className="text-[#A1A1AA] text-sm">({appeal.user_email})</span>
                        </div>
                        <p className="text-[#A1A1AA] text-sm">
                          Enviado em: {new Date(appeal.created_at).toLocaleDateString('pt-BR', { 
                            day: '2-digit', 
                            month: '2-digit', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <Button
                        onClick={() => setViewAppeal(appeal)}
                        className="btn-secondary"
                        data-testid={`view-appeal-${appeal.id}`}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Ver Reivindicação
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Users Table */}
            <div className="glass-card p-4 md:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <p className="text-white font-semibold text-lg">
                  Total: <span className="text-[#FF5A00]">{users.length}</span> usuários
                </p>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-[#A1A1AA]">Usuário</TableHead>
                      <TableHead className="text-[#A1A1AA]">Email</TableHead>
                      <TableHead className="text-[#A1A1AA]">Time</TableHead>
                      <TableHead className="text-[#A1A1AA]">Status</TableHead>
                      <TableHead className="text-[#A1A1AA]">Cadastrado em</TableHead>
                      <TableHead className="text-[#A1A1AA] text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(user => (
                      <TableRow key={user.user_id} className="border-white/10">
                        <TableCell className="text-white">
                          <div className="flex items-center gap-3">
                            {user.picture ? (
                              <img 
                                src={user.picture} 
                                alt={user.name}
                                className="w-8 h-8 rounded-full"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-[#FF5A00]/20 flex items-center justify-center">
                                <Users className="h-4 w-4 text-[#FF5A00]" />
                              </div>
                            )}
                            <span className="font-medium">{user.name}</span>
                          </div>
                        </TableCell>
                      <TableCell className="text-[#A1A1AA]">{user.email}</TableCell>
                      <TableCell className="text-white">
                        {user.has_team ? (
                          <div>
                            <span className="font-medium">{user.team_name}</span>
                            <span className={`ml-2 text-xs ${user.team_status === 'registered' ? 'text-[#00FF66]' : 'text-yellow-500'}`}>
                              ({user.team_status === 'registered' ? 'Cadastrado' : 'Rascunho'})
                            </span>
                          </div>
                        ) : (
                          <span className="text-[#A1A1AA]">Sem time</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.is_banned ? (
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-500">
                              Banido
                            </span>
                            {user.ban_reason && (
                              <span className="text-[#A1A1AA] text-xs" title={user.ban_reason}>
                                ({user.ban_reason.substring(0, 20)}...)
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500/10 text-[#00FF66]">
                            Ativo
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-[#A1A1AA]">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR') : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {user.is_banned ? (
                            <Button
                              onClick={() => handleUnbanUser(user.user_id)}
                              variant="ghost"
                              size="sm"
                              className="text-[#00FF66] hover:text-green-400 hover:bg-green-500/10"
                              title="Desbanir usuário"
                              data-testid={`unban-user-${user.user_id}`}
                            >
                              <ShieldCheck className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              onClick={() => setBanUserId(user.user_id)}
                              variant="ghost"
                              size="sm"
                              className="text-yellow-500 hover:text-yellow-400 hover:bg-yellow-500/10"
                              title="Banir usuário"
                              data-testid={`ban-user-${user.user_id}`}
                            >
                              <ShieldAlert className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            onClick={() => setDeleteUserId(user.user_id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                            title="Excluir usuário"
                            data-testid={`delete-user-${user.user_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {users.length === 0 && (
              <div className="text-center py-12">
                <p className="text-[#A1A1AA] text-lg">Nenhum usuário encontrado</p>
              </div>
            )}
            </div>
          </div>
        )}

        {/* Admins Tab */}
        {activeTab === 'admins' && (
          <div className="glass-card p-4 md:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <p className="text-white font-semibold text-lg">
                Total: <span className="text-[#FF5A00]">{adminUsers.length}</span> administradores
              </p>
              <Button
                onClick={() => setShowAddAdmin(true)}
                className="btn-primary"
                data-testid="add-admin-button"
              >
                <Plus className="mr-2 h-4 w-4" />
                Novo Administrador
              </Button>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white font-semibold">Nome</TableHead>
                    <TableHead className="text-white font-semibold">Email</TableHead>
                    <TableHead className="text-white font-semibold">Criado em</TableHead>
                    <TableHead className="text-white font-semibold text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {adminUsers.map((admin) => (
                    <TableRow key={admin.admin_id} className="border-white/10 hover:bg-white/5" data-testid={`admin-row-${admin.admin_id}`}>
                      <TableCell className="text-white font-medium">
                        {admin.name}
                        {admin.admin_id === currentAdmin?.admin_id && (
                          <span className="ml-2 text-xs bg-[#FF5A00]/20 text-[#FF5A00] px-2 py-0.5 rounded">Você</span>
                        )}
                      </TableCell>
                      <TableCell className="text-[#A1A1AA]">{admin.email}</TableCell>
                      <TableCell className="text-[#A1A1AA]">
                        {new Date(admin.created_at).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            onClick={() => setEditAdmin({...admin, originalEmail: admin.email, newPassword: ''})}
                            variant="ghost"
                            size="sm"
                            className="text-blue-500 hover:text-blue-400 hover:bg-blue-500/10"
                            data-testid={`edit-admin-${admin.admin_id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {admin.admin_id !== currentAdmin?.admin_id && (
                            <Button
                              onClick={() => setDeleteAdminId(admin.admin_id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                              data-testid={`delete-admin-${admin.admin_id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* API Tab */}
        {activeTab === 'api' && (
          <div className="space-y-6">
            <Card className="glass-card border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Key className="h-5 w-5 text-[#FF5A00]" />
                  Token de Acesso à API Pública
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Status Toggle */}
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-white font-semibold">Status da API</p>
                    <p className="text-[#A1A1AA] text-sm">
                      {apiTokenActive ? 'A API pública está ativa e aceitando requisições' : 'A API pública está desativada'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-semibold ${apiTokenActive ? 'text-[#00FF66]' : 'text-red-500'}`}>
                      {apiTokenActive ? 'Ativa' : 'Inativa'}
                    </span>
                    <Switch
                      checked={apiTokenActive}
                      onCheckedChange={handleToggleApiToken}
                      disabled={!apiToken}
                      data-testid="api-toggle"
                    />
                  </div>
                </div>

                {/* Token Display */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-white font-semibold">Token de Autenticação</label>
                    <Button
                      onClick={handleGenerateToken}
                      className="btn-secondary"
                      data-testid="generate-token-button"
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      {apiToken ? 'Gerar Novo Token' : 'Gerar Token'}
                    </Button>
                  </div>
                  
                  {apiToken ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={apiToken}
                        readOnly
                        className="bg-white/5 border-white/10 text-white font-mono text-sm"
                      />
                      <Button
                        onClick={() => copyToClipboard(apiToken)}
                        className="btn-secondary shrink-0"
                        data-testid="copy-token-button"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                      <p className="text-yellow-500 text-sm">
                        Nenhum token gerado. Clique em "Gerar Token" para criar um token de acesso.
                      </p>
                    </div>
                  )}
                </div>

                {/* API URL */}
                {apiToken && (
                  <div className="space-y-3">
                    <label className="text-white font-semibold flex items-center gap-2">
                      <Link2 className="h-4 w-4" />
                      URL da API (Todos os Times)
                    </label>
                    <div className="flex items-center gap-2">
                      <Input
                        value={getPublicApiUrl()}
                        readOnly
                        className="bg-white/5 border-white/10 text-white font-mono text-sm"
                      />
                      <Button
                        onClick={() => copyToClipboard(getPublicApiUrl())}
                        className="btn-secondary shrink-0"
                        data-testid="copy-url-button"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-[#A1A1AA] text-sm">
                      Use esta URL para acessar todos os times cadastrados via GET request.
                    </p>
                  </div>
                )}

                {/* API URL with team_name filter */}
                {apiToken && (
                  <div className="space-y-3">
                    <label className="text-white font-semibold flex items-center gap-2">
                      <Trophy className="h-4 w-4" />
                      URL da API (Filtrar por Nome do Time)
                    </label>
                    <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                      <p className="text-[#A1A1AA] text-sm mb-2">Exemplo com filtro <code className="bg-white/10 px-1 rounded">team_name</code>:</p>
                      <code className="text-[#FF5A00] text-sm break-all">
                        {getPublicApiUrl()}&team_name=corujinhas
                      </code>
                      <p className="text-[#A1A1AA] text-xs mt-3">
                        Substitua <code className="bg-white/10 px-1 rounded">corujinhas</code> pelo nome exato do time.
                      </p>
                    </div>
                  </div>
                )}

                {/* Example Response */}
                <div className="space-y-3">
                  <label className="text-white font-semibold">Exemplo de Resposta</label>
                  <div className="bg-black/50 p-4 rounded-lg overflow-x-auto">
                    <pre className="text-[#A1A1AA] text-sm font-mono whitespace-pre">
{`{
  "teams": [
    {
      "id": 1,
      "team_id": "team_abc123",
      "user_id": "user_xyz789",
      "name": "Meu Time FC",
      "formation": "4-3-3",
      "status": "cadastrado",
      "creator": {
        "name": "João Silva",
        "email": "joao@email.com"
      },
      "players": [
        { "name": "Player1", "age": 25, "state": "SP", "type": "real" },
        { "name": "Bot1", "type": "bot" }
      ],
      "summary": {
        "total_players": 11,
        "real_players": 7,
        "bot_players": 4
      }
    }
  ],
  "total": 1
}`}
                    </pre>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Delete Team Confirmation */}
      <AlertDialog open={!!deleteTeamId} onOpenChange={() => setDeleteTeamId(null)}>
        <AlertDialogContent className="bg-[#14151C] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              Tem certeza que deseja excluir este time? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-secondary">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTeam} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Admin Confirmation */}
      <AlertDialog open={!!deleteAdminId} onOpenChange={() => setDeleteAdminId(null)}>
        <AlertDialogContent className="bg-[#14151C] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              Tem certeza que deseja excluir este administrador? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-secondary">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAdmin} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Ban User Confirmation */}
      <AlertDialog open={!!banUserId} onOpenChange={() => { setBanUserId(null); setBanReason(''); }}>
        <AlertDialogContent className="bg-[#14151C] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Banir Usuário</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              O usuário será impedido de acessar a plataforma. Você pode desbanir depois se necessário.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <label className="text-white text-sm font-medium mb-2 block">Motivo do banimento (opcional)</label>
            <Input
              value={banReason}
              onChange={(e) => setBanReason(e.target.value)}
              placeholder="Ex: Violação das regras..."
              className="bg-white/5 border-white/10 text-white"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-secondary">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleBanUser} className="bg-yellow-600 hover:bg-yellow-700">
              Banir Usuário
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete User Confirmation */}
      <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
        <AlertDialogContent className="bg-[#14151C] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Excluir Usuário</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              Tem certeza que deseja excluir este usuário? Todos os dados do usuário (time, jogadores) serão removidos permanentemente. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-secondary">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700">
              Excluir Permanentemente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Ban Appeal Dialog */}
      <Dialog open={!!viewAppeal} onOpenChange={() => setViewAppeal(null)}>
        <DialogContent className="bg-[#14151C] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-yellow-500" />
              Reivindicação de Banimento
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Analise a justificativa do usuário
            </DialogDescription>
          </DialogHeader>
          {viewAppeal && (
            <div className="space-y-4">
              <div className="bg-white/5 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#FF5A00]/20 flex items-center justify-center">
                    <Users className="h-5 w-5 text-[#FF5A00]" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{viewAppeal.user_name}</p>
                    <p className="text-[#A1A1AA] text-sm">{viewAppeal.user_email}</p>
                  </div>
                </div>
                <p className="text-[#A1A1AA] text-xs">
                  Enviado em: {new Date(viewAppeal.created_at).toLocaleDateString('pt-BR', { 
                    day: '2-digit', 
                    month: '2-digit', 
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Justificativa do usuário:</label>
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <p className="text-white text-sm leading-relaxed">{viewAppeal.message}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button
              onClick={() => setViewAppeal(null)}
              variant="outline"
              className="flex-1 border-white/20 text-white hover:bg-white/10"
            >
              Fechar
            </Button>
            <Button
              onClick={() => handleRejectAppeal(viewAppeal?.id)}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              Manter Banido
            </Button>
            <Button
              onClick={() => handleAcceptAppeal(viewAppeal?.user_id)}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              Desbanir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Team Dialog */}
      <Dialog open={!!viewTeam} onOpenChange={() => setViewTeam(null)}>
        <DialogContent className="bg-[#14151C] border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">{viewTeam?.team_name}</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Detalhes do time • {viewTeam?.formation}
            </DialogDescription>
          </DialogHeader>
          {viewTeam && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {getStatusBadge(viewTeam.status)}
                {viewTeam.rejection_reason && (
                  <span className="text-red-400 text-sm">Motivo: {viewTeam.rejection_reason}</span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-[#A1A1AA]">Criador:</span>
                  <p className="text-white">{viewTeam.creator_name}</p>
                </div>
                <div>
                  <span className="text-[#A1A1AA]">Email:</span>
                  <p className="text-white">{viewTeam.creator_email}</p>
                </div>
              </div>
              <div>
                <span className="text-[#A1A1AA] text-sm">Jogadores ({viewTeam.players?.length || 0}):</span>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                  {(viewTeam.players || []).map((player, idx) => (
                    <div key={idx} className="bg-white/5 p-3 rounded-lg flex items-center gap-3">
                      {player.photo_url ? (
                        <img src={player.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-[#A1A1AA]">
                          {player.type === 'bot' ? '🤖' : '?'}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {player.game_tag || (player.type === 'bot' ? 'Bot' : 'Slot ' + player.slot_number)}
                        </p>
                        <p className="text-[#A1A1AA] text-xs">
                          {player.type === 'real' ? `${player.age} anos • ${player.state}` : 'Bot'}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${player.type === 'real' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                        {player.type === 'real' ? 'Real' : 'Bot'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Team Dialog */}
      <Dialog open={!!editTeam} onOpenChange={() => setEditTeam(null)}>
        <DialogContent className="bg-[#14151C] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Editar Time</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Altere o nome do time
            </DialogDescription>
          </DialogHeader>
          {editTeam && (
            <div className="space-y-4">
              <div>
                <label className="text-white text-sm mb-2 block">Nome do Time</label>
                <Input
                  value={editTeam.team_name}
                  onChange={(e) => setEditTeam({...editTeam, team_name: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditTeam(null)} className="text-[#A1A1AA]">Cancelar</Button>
            <Button onClick={handleUpdateTeam} className="btn-primary">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Admin Dialog */}
      <Dialog open={showAddAdmin} onOpenChange={setShowAddAdmin}>
        <DialogContent className="bg-[#14151C] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Novo Administrador</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Crie um novo usuário administrador
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-white text-sm mb-2 block">Nome</label>
              <Input
                value={newAdmin.name}
                onChange={(e) => setNewAdmin({...newAdmin, name: e.target.value})}
                placeholder="Nome do administrador"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <label className="text-white text-sm mb-2 block">Email</label>
              <Input
                type="email"
                value={newAdmin.email}
                onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                placeholder="email@exemplo.com"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            <div>
              <label className="text-white text-sm mb-2 block">Senha</label>
              <Input
                type="password"
                value={newAdmin.password}
                onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                placeholder="••••••••"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddAdmin(false)} className="text-[#A1A1AA]">Cancelar</Button>
            <Button onClick={handleCreateAdmin} className="btn-primary">Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Admin Dialog */}
      <Dialog open={!!editAdmin} onOpenChange={() => setEditAdmin(null)}>
        <DialogContent className="bg-[#14151C] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Editar Administrador</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Altere as informações do administrador
            </DialogDescription>
          </DialogHeader>
          {editAdmin && (
            <div className="space-y-4">
              <div>
                <label className="text-white text-sm mb-2 block">Nome</label>
                <Input
                  value={editAdmin.name}
                  onChange={(e) => setEditAdmin({...editAdmin, name: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="text-white text-sm mb-2 block">Email</label>
                <Input
                  type="email"
                  value={editAdmin.email}
                  onChange={(e) => setEditAdmin({...editAdmin, email: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="text-white text-sm mb-2 block">
                  <Lock className="inline h-4 w-4 mr-1" />
                  Nova Senha (deixe em branco para manter)
                </label>
                <Input
                  type="password"
                  value={editAdmin.newPassword || ''}
                  onChange={(e) => setEditAdmin({...editAdmin, newPassword: e.target.value})}
                  placeholder="••••••••"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditAdmin(null)} className="text-[#A1A1AA]">Cancelar</Button>
            <Button onClick={handleUpdateAdmin} className="btn-primary">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
